// react
import { ReactNode } from 'react';

// router
import { LinkProps as RouterLinkProps } from 'react-router-dom';

// mui
import {
	Button,
	ButtonProps,
	Chip,
	ChipProps,
	Grid,
	Paper,
	PaperProps as MuiPaperProps,
	SxProps,
	Typography,
	TypographyProps,
	GridProps,
} from '@mui/material';

// icons
import { ArrowForward as IconArrowForward } from '@mui/icons-material';

// ui
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';

// theme
import { theme } from '../../../theme';

// props
export type CardActionButtonProps = ButtonProps & {
	to?: RouterLinkProps['to'];
};

export interface CardActionProps {
	action?: TypographyProps;
	ButtonProps?: CardActionButtonProps;
	ChipProps?: ChipProps | null;
	content?: TypographyProps;
	GridProps?: GridProps;
	hasBreakLine?: boolean;
	icon?: ReactNode;
	IconCircleProps: IconCircleProps;
	PaperProps?: MuiPaperProps;
	title: TypographyProps;
	titleGridProps?: GridProps;
}

export const CardAction = ({
	action,
	ButtonProps = {},
	ChipProps,
	content,
	hasBreakLine = true,
	icon,
	IconCircleProps = { size: 'sm' },
	PaperProps,
	title,
	titleGridProps,
}: CardActionProps) => {
	// vars
	const labelArray =
		title?.children && typeof title?.children === 'string' ? title.children.split(' ') : [];
	const firstWord = labelArray.shift();

	let colorAction = theme.palette.brand.blue[500];
	let colorTitle: string | undefined = theme.palette.common.black;

	if (ButtonProps.disabled) {
		colorAction = theme.palette.brand.grey[400];
		colorTitle = theme.palette.brand.grey[500];
	}

	const { children, ...titleProps } = title;

	let titleChildren = title?.children;

	if (hasBreakLine) {
		titleChildren = (
			<>
				{firstWord}
				<br />
				{labelArray.join(' ')}
			</>
		);
	}

	return (
		<Paper
			sx={(theme) => {
				const sx: SxProps = {
					minHeight: '90px',
					height: '100%',
				};

				return sx;
			}}
			{...PaperProps}>
			<Button
				color="primary"
				fullWidth={true}
				variant="outlined"
				{...ButtonProps}
				sx={{
					height: '100%',
					padding: theme.spacing(2),
					color: 'black',
					'&.Mui-disabled': {
						border: 'none',
					},
					...ButtonProps?.sx,
				}}>
				<Grid container height="100%">
					<Grid item xs={12}>
						<Grid container justifyContent="space-between" spacing={2}>
							<Grid item xs={true} lg={12}>
								<Grid container columnSpacing={1} rowSpacing={2} {...titleGridProps}>
									<Grid item xs="auto">
										<IconCircle {...IconCircleProps} />
									</Grid>
									<Grid item xs={true}>
										<Typography align="left" color={colorTitle} fontWeight={600} {...titleProps}>
											{titleChildren}
										</Typography>
									</Grid>
									{content && (
										<Grid item xs={12}>
											<Typography align="left" color="brand.grey.500" {...content} />
										</Grid>
									)}
								</Grid>
							</Grid>
							{ChipProps && (
								<Grid item xs="auto">
									<Chip {...ChipProps} />
								</Grid>
							)}
						</Grid>
					</Grid>

					<Grid alignSelf="flex-end" item xs={12}>
						<Grid alignItems="center" container justifyContent="flex-end" spacing={1}>
							{action && (
								<Grid item>
									<Typography color={colorAction} component="span" fontWeight={600} {...action} />
								</Grid>
							)}

							<Grid item sx={{ color: colorAction }}>
								{icon || <IconArrowForward />}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Button>
		</Paper>
	);
};
